/** Rate Plan list page with table view and rate management actions. */
import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Assignment as AssignIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import RatePlanCreateDialog from "../../components/rate-plans/RatePlanCreateDialog";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
  SortingState,
  flexRender,
} from "@tanstack/react-table";
import apiClient from "../../api/client";

/** API base URL. */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Rate Plan interface matching backend schema. */
export interface RatePlan {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  is_active: boolean;
  rate_count: number;
  created_at: string;
  updated_at: string;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<RatePlan>();

/** Rate Plan List component. */
export default function RatePlanList() {
  const navigate = useNavigate();
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<RatePlan | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  /** Fetch rate plans from API. */
  const fetchRatePlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const page = pagination.pageIndex + 1;
      const response = await apiClient.get(
        `${API_BASE_URL}/api/v1/rate-plans/?page=${page}&page_size=${pagination.pageSize}`
      );
      setRatePlans(response.data.rate_plans);
      setTotalCount(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load rate plans");
    } finally {
      setLoading(false);
    }
  };

  /** Delete rate plan. */
  const deleteRatePlan = async () => {
    if (!planToDelete) return;

    try {
      await apiClient.delete(`${API_BASE_URL}/api/v1/rate-plans/${planToDelete.id}`);
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      fetchRatePlans(); // Refresh list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete rate plan");
    }
  };

  /** Handle delete button click. */
  const handleDeleteClick = (plan: RatePlan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  /** Navigate to rate plan detail view. */
  const handleViewPlan = (planId: string) => {
    navigate(`/rate-plans/${planId}`);
  };

  /** Handle create dialog close. */
  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  /** Handle successful rate plan creation. */
  const handleCreateSuccess = (ratePlanId: string) => {
    // Navigation is handled by the dialog itself
    setCreateDialogOpen(false);
  };

  /** Fetch rate plans when pagination or sorting changes. */
  useEffect(() => {
    fetchRatePlans();
  }, [pagination.pageIndex, pagination.pageSize]);

  /** Define table columns. */
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => (
          <Button
            color="primary"
            onClick={() => handleViewPlan(info.row.original.id)}
            sx={{ textTransform: "none", fontWeight: "bold" }}
          >
            {info.getValue()}
          </Button>
        ),
      }),
      columnHelper.accessor("description", {
        header: "Description",
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("created_by", {
        header: "Created By",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("rate_count", {
        header: "Rate Count",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("is_active", {
        header: "Status",
        cell: (info) => (
          <Chip
            label={info.getValue() ? "Active" : "Inactive"}
            color={info.getValue() ? "success" : "default"}
            size="small"
          />
        ),
      }),
      columnHelper.accessor("created_at", {
        header: "Created At",
        cell: (info) => new Date(info.getValue()).toLocaleString(),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (props) => (
          <Box>
            <Tooltip title="View Rates">
              <IconButton
                size="small"
                onClick={() => handleViewPlan(props.row.original.id)}
                color="primary"
               aria-label="View">
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                aria-label="Edit"
                onClick={() => navigate(`/rate-plans/${props.row.original.id}?edit=1`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(props.row.original)}
                color="error"
               aria-label="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      }),
    ],
    []
  );

  /** Initialize TanStack Table. */
  const table = useReactTable({
    data: ratePlans,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      pagination,
    },
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    manualPagination: true,
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Rate Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Rate Plan
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Rate Plans Table */}
      {!loading && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell key={header.id}>
                        {header.column.getCanSort() ? (
                          <TableSortLabel
                            active={header.column.getIsSorted() !== false}
                            direction={header.column.getIsSorted() === "asc" ? "asc" : "desc"}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </TableSortLabel>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                      {/* Wave 3.7: empty state with CTA (UX review §3.2) */}
                      <Typography variant="h6" gutterBottom>
                        No rate plans yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Rate plans define what your customers pay. Create your first
                        plan, then add destination rates to it.
                      </Typography>
                      <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
                        Create rate plan
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
            <Typography variant="body2">
              Showing {pagination.pageIndex * pagination.pageSize + 1} to{" "}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)} of{" "}
              {totalCount} rate plans
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.setPageIndex(0)}
                disabled={pagination.pageIndex === 0}
              >
                First
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.previousPage()}
                disabled={pagination.pageIndex === 0}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.nextPage()}
                disabled={table.getPageCount() === 0 || pagination.pageIndex >= table.getPageCount() - 1}
              >
                Next
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={table.getPageCount() === 0 || pagination.pageIndex >= table.getPageCount() - 1}
              >
                Last
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Rate Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete rate plan "{planToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Warning: This will delete all rates and customer assignments for this plan.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteRatePlan} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Rate Plan Dialog */}
      <RatePlanCreateDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        onCreateSuccess={handleCreateSuccess}
      />
    </Box>
  );
}
